import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Alert,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Backdrop,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { getEquipments } from "../api/equipment";
import api from "../api/api";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

// ====================== TIME ======================
function utcToLocalString(str) {
  if (!str || typeof str !== "string") return "-";
  const dt = new Date(str);
  if (isNaN(dt.getTime())) return "-";

  const dia = dt.getDate().toString().padStart(2, "0");
  const mes = (dt.getMonth() + 1).toString().padStart(2, "0");
  const ano = dt.getFullYear().toString().slice(-2);
  const hora = dt.getHours().toString().padStart(2, "0");
  const min = dt.getMinutes().toString().padStart(2, "0");

  return `${dia}/${mes}/${ano} ${hora}:${min}`;
}

const formatDateUTCForSQL = (dateLike) => {
  if (!dateLike) return "";

  const date = typeof dateLike?.toDate === "function" ? dateLike.toDate() : dateLike;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 19).replace("T", " ");
};

// ====================== PERFIS ======================
const PERFIS_EQUIPAMENTO = {
  HABITAT: ["CO2", "PM1", "PM2_5", "PM10", "TEMP", "UMID", "PRESSAO", "VOC", "NOX"],
  MED: ["CO2", "PM1", "PM2_5", "PM10", "TEMP", "UMID", "PRESSAO", "VOC_ppm", "HCHO"],
  PRO: ["PM1", "PM2_5", "PM10", "TEMP", "UMID", "CO", "H2S", "VOC", "NOX"],
};

const DEFAULT_PARAMS_BY_MODEL = {
  HABITAT: ["CO2", "PM2_5"],
  MED: ["CO2", "HCHO"],
  PRO: ["CO", "H2S"],
};

function normalizeModel(value) {
  const s = (value || "").toString().trim().toUpperCase();
  if (!s) return "";
  if (s.includes("HABITAT")) return "HABITAT";
  if (s.includes("MED")) return "MED";
  if (s.includes("PRO")) return "PRO";
  return "";
}

function detectModelFromEquipment(eq) {
  if (!eq) return "HABITAT";

  const directFields = [
    eq.model,
    eq.modelo,
    eq.type,
    eq.tipo,
    eq.profile,
    eq.perfil,
    eq.equipment_type,
    eq.equipmentType,
    eq.device_type,
    eq.deviceType,
    eq.category,
    eq.categoria,
  ];

  for (const value of directFields) {
    const model = normalizeModel(value);
    if (model) return model;
  }

  const combined = [eq.location, eq.name, eq.nome, eq.description, eq.descricao].filter(Boolean).join(" ");
  return normalizeModel(combined) || "HABITAT";
}

function getAllowedKeysByModel(model) {
  return new Set(PERFIS_EQUIPAMENTO[model] || PERFIS_EQUIPAMENTO.HABITAT);
}

function getDefaultSelection(model, activeKeys = []) {
  const defaults = DEFAULT_PARAMS_BY_MODEL[model] || DEFAULT_PARAMS_BY_MODEL.HABITAT;
  const available = defaults.filter((k) => activeKeys.includes(k));
  if (available.length) return available;
  return activeKeys.slice(0, Math.min(2, activeKeys.length));
}

// ====================== NORMALIZAÇÃO SENSOR ======================
function normSensorKey(raw) {
  const s = (raw || "")
    .toString()
    .trim()
    .replace(/\./g, "_")
    .replace(/\s+/g, "_")
    .toUpperCase();

  if (s === "PM2_5" || s === "PM25") return "PM2_5";
  if (s === "VOC_PPM") return "VOC_ppm";
  if (s === "PRESSAO" || s === "PRESSURE") return "PRESSAO";
  if (s === "TEMP" || s === "TEMPERATURA" || s === "TEMPERATURE") return "TEMP";
  if (s === "UMID" || s === "UMIDADE" || s === "HUMIDITY" || s === "RH") return "UMID";
  if (s === "NOX") return "NOX";
  if (s === "HCHO" || s === "FORMALDEHYDE" || s === "FORMALDEIDO") return "HCHO";
  if (s === "VOC") return "VOC";
  if (s === "CO" || s === "CO_PPM") return "CO";
  if (s === "H2S" || s === "H2S_PPM") return "H2S";

  return s;
}

// ====================== CORES ======================
const parametroCores = {
  CO2: "#43a047",
  PRESSAO: "#ab47bc",
  TEMP: "#ff7043",
  UMID: "#1e88e5",
  PM1: "#bdbdbd",
  PM2_5: "#ffa726",
  PM10: "#e65100",
  VOC: "#00c853",
  VOC_ppm: "#00c853",
  NOX: "#90caf9",
  HCHO: "#26a69a",
  CO: "#fdd835",
  H2S: "#8d6e63",
};

// ====================== TOOLTIP ======================
function CustomTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    return (
      <Paper elevation={3} sx={{ p: 1.2, bgcolor: "background.paper" }}>
        <Typography fontWeight={600} fontSize={14}>
          Data/Hora: {payload[0]?.payload?.timestamp}
        </Typography>
        {payload.map((pld, idx) => {
          let val = pld.value;
          if (typeof val === "number") {
            val = Number.isInteger(val) ? val : val.toFixed(2).replace(".", ",");
          }
          return (
            <Typography key={idx} sx={{ color: pld.color }}>
              {pld.name || pld.dataKey}: <b>{val}</b>
            </Typography>
          );
        })}
      </Paper>
    );
  }
  return null;
}

// ====================== BASE PARAMS ======================
const PARAMS_BASE = [
  { key: "CO2", label: "CO₂", axis: "main" },
  { key: "PM2_5", label: "MP2.5", axis: "main" },
  { key: "PM10", label: "MP10", axis: "main" },
  { key: "PM1", label: "MP1", axis: "main" },
  { key: "TEMP", label: "Temperatura", axis: "main" },
  { key: "UMID", label: "Umidade", axis: "main" },
  { key: "PRESSAO", label: "Pressão", axis: "main" },

  // MED
  { key: "VOC_ppm", label: "VOC (ppm)", axis: "main" },
  { key: "HCHO", label: "Formaldeído (HCHO)", axis: "main" },

  // Habitat / Pro
  { key: "VOC", label: "VOC (index)", axis: "index" },
  { key: "NOX", label: "NOx (index)", axis: "index" },

  // PRO
  { key: "CO", label: "CO (ppm)", axis: "main" },
  { key: "H2S", label: "H₂S (ppm)", axis: "main" },
];

export default function Historico() {
  const [equipments, setEquipments] = useState([]);
  const [mac, setMac] = useState("");

  const equipamentoSelecionado = useMemo(
    () => equipments.find((e) => e.mac === mac) || null,
    [equipments, mac]
  );

  const modeloSelecionado = useMemo(
    () => detectModelFromEquipment(equipamentoSelecionado),
    [equipamentoSelecionado]
  );

  const [paramsAtivos, setParamsAtivos] = useState(PARAMS_BASE);
  const [paramsSel, setParamsSel] = useState(["CO2"]);

  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    now.setHours(now.getHours() - 1, 0, 0, 0);
    return now;
  });

  const [endDate, setEndDate] = useState(() => new Date());

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [erro, setErro] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    getEquipments().then((list) => {
      setEquipments(list || []);
      if (!mac && list?.length) setMac(list[0].mac);
      if (mac && list?.length && !list.find((e) => e.mac === mac)) setMac(list[0].mac);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const allowed = getAllowedKeysByModel(modeloSelecionado);
    const ativosPerfil = PARAMS_BASE.filter((p) => allowed.has(p.key));
    const activeKeys = ativosPerfil.map((p) => p.key);

    setData([]);
    setErro("");
    setParamsAtivos(ativosPerfil);
    setParamsSel(getDefaultSelection(modeloSelecionado, activeKeys));
  }, [mac, modeloSelecionado]);

  const fetchHistorico = async () => {
    setErro("");
    if (!mac || !startDate || !endDate) return;

    if (endDate < startDate) {
      setErro("Período inválido: a data final está antes da inicial.");
      return;
    }

    const startStr = formatDateUTCForSQL(startDate);
    const endStr = formatDateUTCForSQL(endDate);

    setLoading(true);
    try {
      const res = await api.get("/historic-data", {
        params: { mac, start_date: startStr, end_date: endStr },
      });

      const rows = Array.isArray(res.data) ? res.data : [];
      const presentes = new Set(rows.map((r) => normSensorKey(r.sensor)));
      const allowed = getAllowedKeysByModel(modeloSelecionado);

      const isMED = modeloSelecionado === "MED" || presentes.has("HCHO");
      const hasVocPpm = presentes.has("VOC_ppm");
      const hasVoc = presentes.has("VOC");
      const vocPpmAtivo = hasVocPpm || (isMED && hasVoc);

      const ativos = PARAMS_BASE.filter((p) => {
        if (!allowed.has(p.key)) return false;
        if (isMED && (p.key === "VOC" || p.key === "NOX")) return false;
        if (p.key === "VOC_ppm") return vocPpmAtivo;
        if (p.key === "VOC") return !isMED && hasVoc && !hasVocPpm;
        if (p.key === "NOX") return !isMED && presentes.has("NOX");
        return presentes.has(p.key);
      });

      const ativosSet = new Set(ativos.map((a) => a.key));

      const obj = {};
      for (const item of rows) {
        const ts = item.timestamp;
        if (!ts) continue;

        let k = normSensorKey(item.sensor);
        if (isMED && k === "VOC") k = "VOC_ppm";

        if (!ativosSet.has(k)) continue;

        const num = Number(item.value);
        if (!Number.isFinite(num)) continue;

        if (!obj[ts]) obj[ts] = { timestampISO: ts, timestamp: ts };
        obj[ts][k] = num;
      }

      const arr = Object.values(obj)
        .sort((a, b) => new Date(a.timestampISO) - new Date(b.timestampISO))
        .map((i) => ({
          ...i,
          timestamp: i.timestampISO ? utcToLocalString(i.timestampISO) : "",
        }));

      setParamsAtivos(ativos);
      setParamsSel((prev) => {
        const filtrado = (prev || []).filter((k) => ativosSet.has(k));
        if (filtrado.length) return filtrado;
        return getDefaultSelection(modeloSelecionado, ativos.map((a) => a.key));
      });
      setData(arr);
    } catch (e) {
      const allowed = getAllowedKeysByModel(modeloSelecionado);
      const ativosPerfil = PARAMS_BASE.filter((p) => allowed.has(p.key));
      setErro("Erro ao buscar dados. Verifique autenticação e conexão.");
      setData([]);
      setParamsAtivos(ativosPerfil);
      setParamsSel(getDefaultSelection(modeloSelecionado, ativosPerfil.map((p) => p.key)));
    } finally {
      setLoading(false);
    }
  };

  const handleCheckbox = (key) => {
    setParamsSel((sel) => (sel.includes(key) ? sel.filter((p) => p !== key) : [...sel, key]));
  };

  const paramsSelValidos = useMemo(() => {
    const s = new Set(paramsAtivos.map((p) => p.key));
    return (paramsSel || []).filter((k) => s.has(k));
  }, [paramsSel, paramsAtivos]);

  const axisByKey = useMemo(() => {
    const map = {};
    for (const p of PARAMS_BASE) map[p.key] = p.axis || "main";
    return map;
  }, []);

  return (
    <Box sx={{ px: { xs: 1, sm: 3 }, py: { xs: 1, sm: 2 }, maxWidth: "100vw", width: "100%" }}>
      <Backdrop open={loading} sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Typography variant={isMobile ? "h6" : "h4"} gutterBottom>
        Histórico dos Parâmetros
      </Typography>

      <Paper
        sx={{
          p: { xs: 1.2, sm: 3 },
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 2 },
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <FormControl sx={{ minWidth: 160, mr: { sm: 2 }, width: { xs: "100%", sm: "auto" } }}>
          <InputLabel>Ambiente</InputLabel>
          <Select
            value={mac}
            label="Ambiente"
            onChange={(e) => setMac(e.target.value)}
            size={isMobile ? "small" : "medium"}
          >
            {equipments.map((e) => (
              <MenuItem key={e.mac} value={e.mac}>
                {e.location} ({e.mac})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormGroup row sx={{ flexWrap: "wrap", maxWidth: { xs: "100%", sm: 720 }, width: { xs: "100%", sm: "auto" } }}>
          {paramsAtivos.map((p) => (
            <FormControlLabel
              key={p.key}
              control={
                <Checkbox
                  checked={paramsSelValidos.includes(p.key)}
                  onChange={() => handleCheckbox(p.key)}
                  size={isMobile ? "small" : "medium"}
                />
              }
              label={p.label}
              sx={{ mr: 1, fontSize: { xs: 12, sm: 15 } }}
            />
          ))}
        </FormGroup>

        <DateTimePicker
          label="Dados iniciais"
          value={startDate}
          onChange={(value) => setStartDate(value)}
          sx={{ width: { xs: "100%", sm: 180 }, mr: { sm: 2 } }}
          slotProps={{ textField: { size: isMobile ? "small" : "medium" } }}
        />
        <DateTimePicker
          label="Dados finais"
          value={endDate}
          onChange={(value) => setEndDate(value)}
          sx={{ width: { xs: "100%", sm: 180 }, mr: { sm: 2 } }}
          slotProps={{ textField: { size: isMobile ? "small" : "medium" } }}
        />

        <Button
          variant="contained"
          sx={{ mt: { xs: 1, sm: 0 }, minWidth: isMobile ? "100%" : 120 }}
          onClick={fetchHistorico}
          size={isMobile ? "small" : "medium"}
        >
          BUSCAR
        </Button>
      </Paper>

      <Paper sx={{ p: { xs: 1.2, sm: 3 }, minHeight: 280, width: "100%", overflowX: "auto" }}>
        {erro && <Alert severity="error">{erro}</Alert>}

        {data.length ? (
          <Box sx={{ width: "100%", minWidth: 300, height: isMobile ? 260 : 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" minTickGap={28} />
                <YAxis yAxisId="main" />
                <YAxis yAxisId="index" orientation="right" domain={[0, 500]} allowDataOverflow />
                <Tooltip content={<CustomTooltip />} />
                <Legend />

                {paramsSelValidos.map((key) => {
                  const p = paramsAtivos.find((x) => x.key === key) || PARAMS_BASE.find((x) => x.key === key);
                  const yAxisId = axisByKey[key] === "index" ? "index" : "main";

                  return (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={p?.label || key}
                      stroke={parametroCores[key] || "#1976d2"}
                      dot={false}
                      strokeWidth={2}
                      connectNulls
                      yAxisId={yAxisId}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          !loading && <Typography color="text.secondary">Nenhum dado para este período/parâmetro.</Typography>
        )}
      </Paper>
    </Box>
  );
}
